// Swatch colours for the two tagging axes.
//
// A row's colour is its identity across the whole app — the sidebar dot, the
// pie segment, the budget rows. It has to come from somewhere at creation time:
// rows used to be created with `color: ''` and fall back to grey at render,
// which meant every classification in a restaurant looked identical in the pie
// and no amount of picking could fix it, because the only screen that could set
// a colour had been orphaned.
//
// So creation assigns one. The user can still change it — both axes offer a
// plain colour picker — and a STORED COLOUR ALWAYS WINS. Everything below is a
// starting point, not a decision taken away from them.
//
// TWO PALETTES, ONE PER AXIS. They used to share one, so a restaurant's first
// קטגוריה and its first סיווג came out the same hue and the two screens read as
// one list. The sets below share no hex at all, which is what makes a swatch's
// axis legible on sight — keep it that way (there is a test).

// ── סיווגי פריטים (item_classifications) ─────────────────────────────────────
//
// Taken from live data, not invented: these are the colours a real chain
// (מסעדת בואו) had settled on across 19 classifications, so they are known to
// work as a set at this size. Muted and low-contrast against each other by
// design — a classification list is long and read as a whole, not as a few
// competing headline figures.
//
// The first eight are the ones that carry that chain's spend, in that order, so
// the restaurants who only ever create a handful get the strongest spread.
export const CLASSIFICATION_PALETTE = [
  '#2b58ab', '#e1db98', '#79a07b', '#abe2ac', '#82b0b0', '#a29dcd', '#8a9e8b', '#8e509f',
  '#a4acbc', '#c99297', '#f0b2e5', '#e5b09a', '#bbddd0', '#40506d', '#6f2715', '#275f63',
  '#7c4dff', '#518a86', '#bddd64',
];

// ── קטגוריות (category_lines) ────────────────────────────────────────────────
//
// The brand sequence, and the older of the two. Twelve, not seven: seven was
// enough while this only had to colour a pie, but קטגוריות runs 3-10 lines and
// the whole screen is those lines next to each other, so at eight the palette
// wrapped and two categories got the identical swatch.
//
// The first five are the fixed brand-chosen sequence (indigo, violet, cyan,
// yellow, coral) — the colours a restaurant with a handful of lines will
// actually see. The remaining seven fill the gaps around the wheel (green,
// orange, pink, teal, orchid, olive, terracotta) so entries 6-12 stay apart
// from the first five and from each other in hue rather than only in name.
//
// ⚠ `utils/supplierSlices.js` SLICE_COLORS repeats the first five as literals.
// Deliberately not imported (that would let a category-palette edit move the
// supplier donut), so the two can drift — change one, check the other.
export const CATEGORY_LINE_PALETTE = [
  '#4A5ABF', '#AA7FFA', '#64C3D3', '#FBDE7F', '#FA8570',
  '#6BCB77', '#F2A65A', '#E87EA1', '#4FC2A0', '#C77DD6', '#B5C46B', '#B07D4F',
];

// "לא מסווג" / "ללא קטגוריה" — the empty bucket on ניתוח רכש — is a synthetic
// row that stands for spend with NO tag, not for a tag of its own. A palette hue
// would make it read as one more real row competing in the pie; a neutral grey
// reads as what it is: the gap the other slices are measured against. A stored
// colour still wins, so this is a default, not a lock.
export const UNTAGGED_COLOR = '#D9D9D9';

// ── the two rules, written once ──────────────────────────────────────────────

/**
 * Stored colour → empty-bucket grey → palette by list position.
 *
 * Order matters: a stored colour always wins ("unless the user changed it").
 * The bucket is recognised by an EXPLICIT null on its own axis key — real rows
 * carry `id` and no such key, so `=== null` never matches them.
 */
function pick(palette, row, index, bucketKey) {
  if (row && row.color) return row.color;
  if (row && row[bucketKey] === null) return UNTAGGED_COLOR;
  return palette[index % palette.length];
}

/**
 * The least-used palette entry among `existing`, so the first N rows are all
 * distinct rather than colliding by luck. Ties break toward the earlier entry,
 * which keeps the choice deterministic — a test can assert it, and two people
 * creating the same row get the same colour.
 *
 * Least-used, not next-index, so deleting a row frees its colour for the next
 * one instead of leaving a gap and marching on.
 */
function leastUsed(palette, existing) {
  const used = new Map(palette.map(c => [c, 0]));
  existing.forEach(c => {
    const key = String(c?.color || '').toLowerCase();
    palette.forEach(p => { if (p.toLowerCase() === key) used.set(p, used.get(p) + 1); });
  });
  return palette.reduce((best, c) => (used.get(c) < used.get(best) ? c : best), palette[0]);
}

// ── the axis-specific pairs ──────────────────────────────────────────────────

/** An item classification's swatch. */
export function classificationColor(cat, index) {
  return pick(CLASSIFICATION_PALETTE, cat, index, 'classification_id');
}

/** A colour for an item classification about to be created. */
export function nextClassificationColor(existing = []) {
  return leastUsed(CLASSIFICATION_PALETTE, existing);
}

/** A category line's swatch. */
export function categoryLineColor(line, index) {
  return pick(CATEGORY_LINE_PALETTE, line, index, 'category_line_id');
}

/** A colour for a category line about to be created. */
export function nextCategoryLineColor(existing = []) {
  return leastUsed(CATEGORY_LINE_PALETTE, existing);
}
